/* eslint-disable react/jsx-props-no-spreading */
import {
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import { myProfile, updateProfile } from '~/queries';
import { notifySaveFailure, notifySaveSuccess } from '~/components/atoms';

import { CertificationsSelect, InputContainer } from '~/components/molecules';
import { JobSeeker } from '~/generated';

type CertificationScoreFormInput = {
  toeicScore: number | string | null;
  certifications: string | undefined;
};

const CertificationScoreForm: React.FC = () => {
  const { data, loading } = useQuery<{ jobSeekerMe: JobSeeker }>(myProfile);
  const [updateSeekerProfile, { error }] = useMutation(updateProfile);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { isDirty, errors },
  } = useForm<CertificationScoreFormInput>({
    mode: 'onBlur',
    shouldFocusError: false,
  });

  useEffect(() => {
    if (!loading && !isDirty) {
      const jobSeeker = {
        certifications: data?.jobSeekerMe?.certifications?.join() || undefined,
      };
      const defaultValues = {
        toeicScore: data?.jobSeekerMe?.toeicScore,
      };
      setValue('toeicScore', data?.jobSeekerMe?.toeicScore || null);
      reset({
        ...defaultValues,
        ...jobSeeker,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const onSubmit: SubmitHandler<CertificationScoreFormInput> = async (
    submittedData,
  ) => {
    const { certifications, toeicScore: toeicScoreString } = submittedData;
    const toeicScore = toeicScoreString === '' ? null : toeicScoreString;
    await updateSeekerProfile({
      variables: {
        input: { certifications, toeicScore },
      },
    });
    if (error) {
      notifySaveFailure();
    } else {
      notifySaveSuccess();
    }
  };

  return (
    <form onBlur={handleSubmit(onSubmit)}>
      <InputContainer
        name="certifications"
        label="保有資格"
        invalid={!!errors.certifications}
      >
        <CertificationsSelect
          onChange={(value) => {
            // setValueは第2引数に空の配列を指定すると値を更新しないのでCSV形式で扱う
            setValue('certifications', value.filter(Boolean).join(), {
              shouldDirty: true,
            });
          }}
        />
      </InputContainer>
      <InputContainer
        name="toeicScore"
        label="TOEICスコア"
        invalid={!!errors.toeicScore}
      >
        <HStack>
          <NumberInput
            w="100px"
            {...register('toeicScore', { required: false })}
            isInvalid={!!errors.toeicScore}
            min={10}
            max={990}
            step={5}
            value={getValues('toeicScore') || undefined}
            onChange={(_valueAsString: string, valueAsNumber: number) => {
              reset({ toeicScore: valueAsNumber });
            }}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </HStack>
      </InputContainer>
    </form>
  );
};

export default CertificationScoreForm;
